import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #0a0a0f; color: #e8e8f0; font-family: 'DM Sans', sans-serif; }
  .legal-wrap { max-width: 780px; margin: 0 auto; padding: 3rem 2rem 5rem; }
  .legal-back { background: none; border: none; color: #00f5a0; font-family: 'DM Sans', sans-serif; font-size: 0.9rem; cursor: pointer; margin-bottom: 2rem; display: flex; align-items: center; gap: 0.4rem; padding: 0; }
  .legal-back:hover { opacity: 0.8; }
  .legal-logo { font-family: 'Bebas Neue', sans-serif; font-size: 1.6rem; background: linear-gradient(90deg, #00f5a0, #00d9f5); -webkit-background-clip: text; -webkit-text-fill-color: transparent; margin-bottom: 3rem; display: block; text-decoration: none; }
  .legal-content h1 { font-family: 'Bebas Neue', sans-serif; font-size: 2.2rem; color: #e8e8f0; margin-bottom: 0.5rem; letter-spacing: 0.02em; }
  .legal-content h2 { font-family: 'Bebas Neue', sans-serif; font-size: 1.4rem; color: #e8e8f0; margin: 2.5rem 0 0.75rem; letter-spacing: 0.02em; }
  .legal-content p, .legal-content li { font-size: 0.95rem; color: #b0b0cc; line-height: 1.8; margin-bottom: 0.75rem; }
  .legal-content ul { padding-left: 1.5rem; margin-bottom: 0.75rem; }
  .legal-content strong { color: #e8e8f0; font-weight: 600; }
  .legal-content a { color: #00f5a0; text-decoration: none; }
  .legal-content a:hover { text-decoration: underline; }
  .legal-date { font-size: 0.82rem; color: #b0b0cc; margin-bottom: 2rem; }
  .legal-divider { border: none; border-top: 1px solid #1e1e2e; margin: 3rem 0; }
  @media (max-width: 600px) { .legal-wrap { padding: 2rem 1rem 4rem; } .legal-content h1 { font-size: 1.7rem; } }
`;

const TERMS_CONTENT = (
  <>
    <h1>Terms of Service</h1>
    <p className="legal-date">Last updated: April 16, 2026 &nbsp;·&nbsp; Effective date: April 16, 2026</p>

    <h2>1. Acceptance of Terms</h2>
    <p>By accessing or using Postlify AI ("the Service") at postlifyai.com, you agree to be bound by these Terms of Service. If you do not agree, please do not use the Service. The Service is operated by <strong>Oy Westmanns Ab</strong> (Business ID: 3296059-1), Finland.</p>

    <h2>2. Description of Service</h2>
    <p>Postlify AI is an AI-powered social media content generation service. Users provide business information, and the Service generates social media posts for platforms including Instagram, LinkedIn, Facebook, and TikTok.</p>

    <h2>3. User Accounts</h2>
    <ul>
      <li>You must provide a valid email address to create an account.</li>
      <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
      <li>You must be at least 18 years old to use the Service.</li>
      <li>One account per person or business entity.</li>
    </ul>

    <h2>4. Free Trial and Subscription</h2>
    <ul>
      <li>New users receive 3 free content generations per day without an account.</li>
      <li>Registered users may subscribe to Postlify AI Pro for €9.99/month.</li>
      <li>Subscriptions are billed monthly and renew automatically.</li>
      <li>You may cancel your subscription at any time. Cancellation takes effect at the end of the current billing period.</li>
      <li>No refunds are provided for partial billing periods.</li>
    </ul>

    <h2>5. Acceptable Use</h2>
    <p>You agree not to:</p>
    <ul>
      <li>Use the Service to generate illegal, harmful, or misleading content</li>
      <li>Attempt to reverse engineer or copy the Service</li>
      <li>Use automated tools to access the Service beyond its intended use</li>
      <li>Violate any applicable laws or regulations</li>
    </ul>

    <h2>6. AI-Generated Content</h2>
    <ul>
      <li>Content generated by Postlify AI is created using artificial intelligence.</li>
      <li>You are responsible for reviewing all generated content before publishing.</li>
      <li>Postlify AI does not guarantee the accuracy, originality, or suitability of generated content.</li>
      <li>Generated content may be similar to content created for other users.</li>
    </ul>

    <h2>7. Intellectual Property</h2>
    <ul>
      <li>You retain ownership of the content you generate using the Service.</li>
      <li>You grant Oy Westmanns Ab a limited license to process your inputs to provide the Service.</li>
      <li>The Service itself, including its design and technology, is owned by Oy Westmanns Ab.</li>
    </ul>

    <h2>8. Limitation of Liability</h2>
    <p>The Service is provided "as is" without warranties of any kind. Oy Westmanns Ab shall not be liable for any indirect, incidental, or consequential damages arising from your use of the Service. Our total liability shall not exceed the amount you paid in the last 3 months.</p>

    <h2>9. Changes to Terms</h2>
    <p>We reserve the right to modify these Terms at any time. We will notify users of significant changes via email. Continued use of the Service constitutes acceptance of the updated Terms.</p>

    <h2>10. Governing Law</h2>
    <p>These Terms are governed by the laws of Finland. Any disputes shall be resolved in the courts of Finland.</p>

    <h2>11. Contact</h2>
    <p><strong>Oy Westmanns Ab</strong><br />Email: <a href="mailto:info@westmanns.fi">info@westmanns.fi</a></p>
  </>
);

const PRIVACY_CONTENT = (
  <>
    <h1>Privacy Policy</h1>
    <p className="legal-date">Last updated: April 16, 2026 &nbsp;·&nbsp; Effective date: April 16, 2026</p>

    <h2>1. Data Controller</h2>
    <p><strong>Oy Westmanns Ab</strong> (Business ID: 3296059-1), Finland<br />Email: <a href="mailto:info@westmanns.fi">info@westmanns.fi</a></p>

    <h2>2. Data We Collect</h2>
    <p><strong>Account data:</strong></p>
    <ul>
      <li>Email address (required for registration)</li>
      <li>Authentication data (managed by Firebase Authentication)</li>
    </ul>
    <p><strong>Usage data:</strong></p>
    <ul>
      <li>Content you enter (industry, product description, tone preferences)</li>
      <li>Generated posts (not stored permanently)</li>
      <li>Session usage count (stored locally in your browser)</li>
    </ul>
    <p><strong>Payment data:</strong></p>
    <ul>
      <li>Payment processing is handled entirely by Stripe. We do not store credit card details.</li>
      <li>Stripe may collect billing information as per their privacy policy.</li>
    </ul>
    <p><strong>Technical data:</strong></p>
    <ul>
      <li>Browser type and device information</li>
      <li>IP address (collected by Firebase and Stripe)</li>
    </ul>

    <h2>3. How We Use Your Data</h2>
    <p>We use your data to:</p>
    <ul>
      <li>Provide and improve the Service</li>
      <li>Process payments and manage subscriptions</li>
      <li>Send service-related communications (e.g., billing, updates)</li>
      <li>Ensure security and prevent abuse</li>
    </ul>

    <h2>4. AI Processing</h2>
    <p>Your input data (industry, product description) is sent to Anthropic's Claude API to generate content. Anthropic processes this data according to their privacy policy. We do not store your input data after the generation is complete.</p>

    <h2>5. Data Sharing</h2>
    <ul>
      <li><strong>Firebase (Google)</strong> — Authentication, hosting, and backend functions</li>
      <li><strong>Stripe</strong> — Payment processing</li>
      <li><strong>Anthropic</strong> — AI content generation</li>
    </ul>
    <p>We do not sell your personal data to third parties.</p>

    <h2>6. Data Retention</h2>
    <ul>
      <li>Account data is retained as long as your account is active.</li>
      <li>You may request deletion of your account and data at any time by contacting <a href="mailto:info@westmanns.fi">info@westmanns.fi</a>.</li>
      <li>Payment records are retained as required by Finnish accounting law (7 years).</li>
    </ul>

    <h2>7. Your Rights (GDPR)</h2>
    <p>As an EU resident, you have the right to:</p>
    <ul>
      <li><strong>Access</strong> your personal data</li>
      <li><strong>Correct</strong> inaccurate data</li>
      <li><strong>Delete</strong> your data ("right to be forgotten")</li>
      <li><strong>Object</strong> to processing of your data</li>
      <li><strong>Data portability</strong> — receive your data in a portable format</li>
      <li><strong>Withdraw consent</strong> at any time</li>
    </ul>
    <p>To exercise these rights, contact: <a href="mailto:info@westmanns.fi">info@westmanns.fi</a></p>

    <h2>8. Cookies</h2>
    <p>The Service uses:</p>
    <ul>
      <li><strong>Essential cookies</strong> — required for authentication and session management</li>
      <li><strong>No advertising or tracking cookies</strong></li>
    </ul>

    <h2>9. Data Security</h2>
    <p>We implement appropriate technical measures to protect your data, including:</p>
    <ul>
      <li>Encrypted connections (HTTPS/SSL)</li>
      <li>Firebase Security Rules for data access control</li>
      <li>API keys stored securely in Firebase Cloud Functions</li>
    </ul>

    <h2>10. Children's Privacy</h2>
    <p>The Service is not intended for users under 18 years of age. We do not knowingly collect data from minors.</p>

    <h2>11. Changes to Privacy Policy</h2>
    <p>We will notify users of significant changes via email. The latest version is always available at postlifyai.com/privacy.</p>

    <h2>12. Contact & Complaints</h2>
    <p>For privacy concerns, contact: <a href="mailto:info@westmanns.fi">info@westmanns.fi</a></p>
    <p>You have the right to lodge a complaint with the Finnish Data Protection Ombudsman (<a href="https://tietosuoja.fi" target="_blank" rel="noopener noreferrer">tietosuoja.fi</a>).</p>
  </>
);

export default function LegalPage({ type }) {
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <style>{STYLES}</style>
      <div className="legal-wrap">
        <button className="legal-back" onClick={() => navigate("/")}>← Back to Postlify AI</button>
        <span className="legal-logo">Postlify AI</span>
        <div className="legal-content">
          {type === "terms" ? TERMS_CONTENT : PRIVACY_CONTENT}
        </div>
      </div>
    </>
  );
}
